import { SearchIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { shopLabels } from '../../data/shopLabels';
import { plural } from '../../core/format/text';
import { btnSecondary, chipCls, inputLineCls } from '../../ui/styles';
import { FilterPanel, SkinChips } from './ShopFilters';
import { ProductGrid } from './ProductGrid';
import { AdviceTile } from './AdviceTile';
import { ShopEmpty } from './ShopEmpty';
import { ADVICE_SLOT, sorts } from './shopRules';
import type { ShopFilterControl } from './useShopFilters';
type Props = {
  f: ShopFilterControl;
  onOpenFilters: () => void;
};

/** Anker van de collectie: de hoofdknop "Kies op je huidvraag" springt hierheen, naar de huidfilters. */
export const COLLECTION_ID = 'collectie';

/** De collectie: filterkolom (desktop), zoeken, sorteren, actieve filters en de grid. */
export function ShopCollection({
  f,
  onOpenFilters
}: Props) {
  const count = f.visible.length;
  return <section id={COLLECTION_ID} className="scroll-mt-24 bg-sand pb-16 pt-6 lg:pb-24 lg:pt-10" aria-label="Collectie" data-id="element-6857" data-mp="2iadc5n0xgasu">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-6858" data-mp="3ebf36esb4480">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-6859" data-mp="3j3yjhjidpdgi">
          {/* Desktop: vaste filterkolom */}
          <aside className="hidden lg:col-span-3 lg:block" aria-label="Filters" data-id="element-6860" data-mp="35dcynp9d6tfz">
            <div className="lg:sticky lg:top-28" data-id="element-6861" data-mp="3aavbhqqi5st9">
              <FilterPanel skin={f.skin} setSkin={f.setSkin} step={f.step} setStep={f.setStep} brand={f.brand} setBrand={f.setBrand} data-id="element-6862" data-mp="3abmtfyr6fs12" />
              {f.hasFilters && <button type="button" onClick={f.clear} className="mt-8 inline-flex items-center gap-1.5 text-[13px] text-clay transition-colors duration-150 ease-soft hover:text-ink" data-id="element-6863" data-mp="5w58vsuns3as">
                  <XIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6864" data-mp="2cnmjrdzwrnit" />
                  Alle filters wissen
                </button>}
            </div>
          </aside>

          {/* min-w-0: anders rekt het scrollrijtje hieronder de kolom op tot buiten het scherm. */}
          <div className="min-w-0 lg:col-span-9" data-id="element-6865" data-mp="1tizrl22gvchf">
            {/* Mobiel: huidtype direct zichtbaar, rest in een lade */}
            <div className="-mx-6 mb-5 flex gap-2 overflow-x-auto px-6 pb-1 [scrollbar-width:none] lg:hidden [&::-webkit-scrollbar]:hidden" data-id="element-6866" data-mp="1orhskjk07lz3">
              <SkinChips skin={f.skin} setSkin={f.setSkin} data-id="element-6867" data-mp="1jsy68i1i0867" />
            </div>

            <div className="flex items-center gap-4" data-id="element-6868" data-mp="1euwktqglnwrk">
              <div className="relative flex-1" data-id="element-6869" data-mp="19va95lgf6okv">
                <SearchIcon className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-6870" data-mp="19th0k0m8xua8" />
                <label htmlFor="shop-search" className="sr-only" data-id="element-6871" data-mp="10uvlwgjwazeq">
                  Zoek in de collectie
                </label>
                <input id="shop-search" type="search" value={f.query} onChange={e => f.setQuery(e.target.value)} placeholder="Zoek op product, ingrediënt of huidtype" className={`${inputLineCls} w-full py-3 pl-7 pr-10 [&::-webkit-search-cancel-button]:hidden`} data-id="element-6872" data-mp="30653p48f3uh4" />
                {f.query && <button type="button" onClick={() => {
                f.setQuery('');
                // Focus terug in het veld: de knop verdwijnt, anders valt de focus naar het begin van de pagina.
                document.getElementById('shop-search')?.focus();
              }} aria-label="Zoekopdracht wissen" className="absolute -right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-6873" data-mp="3cih8zc96bepj">
                    <XIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6874" data-mp="90p7n795dieo" />
                  </button>}
              </div>
              <button type="button" onClick={onOpenFilters} aria-haspopup="dialog" className={`${btnSecondary} shrink-0 lg:hidden`} data-id="element-6875" data-mp="bklnjwwa1bs6">
                <SlidersHorizontalIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6876" data-mp="lkm7nl1yzvxf" />
                Filters
                {f.extraFilterCount > 0 && <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-ink px-1 text-[12px] text-shell" data-id="element-6877" data-mp="ujzm6s4ybap0">
                    <span aria-hidden="true" data-id="element-6878" data-mp="pkemuglvd0br">{f.extraFilterCount}</span>
                    <span className="sr-only" data-id="element-6879" data-mp="14alxia5mxf1w">, {f.extraFilterCount} actief</span>
                  </span>}
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-b border-line pb-4" data-id="element-6880" data-mp="zdbx5zgpjiza">
              <p className="text-[14px] text-muted" aria-live="polite" data-id="element-6881" data-mp="1995kmdvyswvu">
                <span aria-hidden="true" data-id="element-6882" data-mp="1972i4udmlba8">{plural(count, 'product', 'producten')}</span>
                <span className="sr-only" data-id="element-6883" data-mp="1rxza94hghok4">{shopLabels.results(count)}</span>
              </p>
              <div className="flex items-center gap-2" data-id="element-6884" data-mp="1ww0q7gjl57rd">
                <label htmlFor="sort" className="text-[13px] text-muted" data-id="element-6885" data-mp="1ixasbyoxz41y">
                  Sorteren
                </label>
                <select id="sort" value={f.sort} onChange={e => {
                const next = sorts.find(s => s === e.target.value);
                if (next) f.setSort(next);
              }} className={`${inputLineCls} min-h-[44px] py-2 sm:min-h-0 sm:py-1`} data-id="element-6886" data-mp="1nut6c3htg9gt">
                  {sorts.map(s => <option key={s} value={s} data-id="element-6887" data-mp="148oe60b1wicb">
                      {s}
                    </option>)}
                </select>
              </div>
            </div>

            {f.activeChips.length > 0 && <ul className="mt-4 flex flex-wrap gap-2" aria-label="Actieve filters" data-id="element-6888" data-mp="v99oh32pkbsv">
                {f.activeChips.map(c => <li key={c.label} data-id="element-6889" data-mp="2s5w6uyu9umpd">
                    <button type="button" onClick={c.onRemove} className={`${chipCls(true)} inline-flex items-center gap-1.5`} data-id="element-6890" data-mp="2s4ukhy7t2dsc">
                      {c.label}
                      <span className="sr-only" data-id="element-6891" data-mp="3fv8cjoqe4cmx">, filter verwijderen</span>
                      <XIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-6892" data-mp="3ft4cjhvm3xrx" />
                    </button>
                  </li>)}
              </ul>}

            {count === 0 ? <ShopEmpty query={f.query} onClear={f.clear} data-id="element-6893" data-mp="31vfroa1joqmv" /> : <>
                <ProductGrid products={f.shown} insert={count > ADVICE_SLOT ? {
              index: ADVICE_SLOT,
              node: <AdviceTile data-id="element-6895" data-mp="1vd0pgl0qjf3k" />
            } : undefined} data-id="element-6894" data-mp="1vfdh5pwap3tt" />
                {f.hasMore && <div className="mt-14 flex flex-col items-center gap-3 border-t border-line pt-10" data-id="element-6896" data-mp="1qez5zyzeddrk">
                    <p className="text-[14px] text-muted" data-id="element-6897" data-mp="1ge6meciqyx66">
                      {f.limit} van {count} producten
                    </p>
                    <button type="button" onClick={f.showMore} className={btnSecondary} data-id="element-6898" data-mp="1qat5avrije5k">
                      Toon meer producten
                    </button>
                  </div>}
              </>}
          </div>
        </div>
      </div>
    </section>;
}